import { Input } from '@mui/base';
import cx from 'classnames';
import { useId } from 'react';
import { useController } from 'react-hook-form';
import type {
  Control,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import typography from '~styles/typography.scss';
import styles from './ControlledFormDatePicker.scss';
import dateRangeStyles from './ControlledFormDateRange.scss';
import { ValidationErrorMessage } from './ValidationErrorMessage';

export interface ControlledFormDatePickerProps<
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
> {
  control: Control<T>;
  name: Name;
  label: string;
  rules?: UseControllerProps<T, Name>['rules'];
  labelClassName?: string;
  placeholder?: string;
  /**
   * Open the native calendar when the field is clicked or focused (default). Turn it off where
   * people type the date: the open calendar takes the keystrokes, and the icon still opens it.
   */
  openPickerOnFocus?: boolean;
}

export const ControlledFormDatePicker = <
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
>({
  control,
  name,
  label,
  rules,
  labelClassName = '',
  placeholder,
  openPickerOnFocus = true,
}: ControlledFormDatePickerProps<T, Name>) => {
  const id = useId();
  const labelId = `label-${id}`;

  const {
    field: { onChange, value, ref },
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: {
      ...rules,
      validate: {
        ...rules?.validate,
      },
    },
  });
  const hasError = !!error;

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!openPickerOnFocus) return;
    try {
      e.target.showPicker?.();
    } catch (onFocusError) {
      if (
        onFocusError instanceof DOMException &&
        onFocusError.name === 'NotAllowedError'
      ) {
        /* Ignore the NotAllowedError.
         *
         * Me and my bad, bad homies
         * Doin' bad, bad things
         * But it feels so good
         *
         * Source: pH-1, “Mr. Bad” (feat. Woo), 2022
         * */
      } else {
        throw onFocusError;
      }
    }
  };
  const handleOnMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.type = 'date';
    if (openPickerOnFocus && 'showPicker' in e.currentTarget) {
      e.currentTarget.showPicker();
    }
  };
  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.target.value) e.target.type = 'text';
  };

  return (
    <div className={cx(dateRangeStyles.container, styles.datePickerContainer)}>
      <label
        id={labelId}
        className={cx({
          [typography.c2_20]: true,
          [dateRangeStyles.labelError]: hasError,
          [labelClassName]: !!labelClassName,
        })}
      >
        {label}
      </label>
      <div
        className={cx(dateRangeStyles.inputsContainer, styles.inputsContainer)}
      >
        <Input
          onChange={onChange}
          onWheel={() => (document.activeElement as HTMLInputElement).blur()}
          placeholder={placeholder || 'Select a date'}
          slotProps={{
            input: {
              ref,
              inputMode: 'none',
              'aria-labelledby': [labelId].join(' '),
              'aria-errormessage': `${name}-error`,
              'aria-invalid': !!error,
              className: cx({
                [typography.t1]: true,
                [dateRangeStyles.inputError]: !!error,
              }),
              onBlur: handleOnBlur,
              onMouseDown: handleOnMouseDown,
              onFocus: handleOnFocus,
            },
          }}
          type={value ? 'date' : 'text'}
          value={value || ''}
        />
        <ValidationErrorMessage error={error} label={label} name={name} />
      </div>
    </div>
  );
};
